import { AttachmentPayload, Media, Report } from '@/models/Report';
import ErrorHandler from '@/support/ErrorHandler';
import { AxiosError } from 'axios';
import { Component, Model, Prop, Vue } from 'vue-property-decorator';
import DefaultDialog from '@/components/dialog/DefaultDialog.vue';
import FileUpload from '@/components/file-upload/FileUpload.vue';
import { bureauAttachmentTypes } from '@/support/ReportAttachments';

@Component<UploadFileDialog>({
  components: {
    DefaultDialog,
    FileUpload,
  },
})
export default class UploadFileDialog extends Vue {
  @Model('report', { default: () => new Report() })
  protected report!: Report;

  @Prop()
  protected attachment!: Media;

  @Prop()
  protected isEvaluation!: boolean;

  protected isLoading = false;

  protected payload: AttachmentPayload[] = [];

  protected close(): void {
    this.$emit('close');
  }

  protected handleNumberInputBlur(): void {
    if (! this.attachment.amount_of_kpi_points) {
      return;
    }

    if (this.attachment.amount_of_kpi_points < 0) {
      this.attachment.amount_of_kpi_points = 0;
    }

    if (this.attachment.amount_of_kpi_points.toString().length > 3) {
      this.attachment.amount_of_kpi_points = Number(this.attachment.amount_of_kpi_points.toString().substring(0, 3));
    }
  }

  protected save(): void {
    if (this.isEditing && this.hasFileUrl) {
      this.updateAttachment();
      return;
    }

    this.uploadFiles();
  }

  protected handleFilesDropped(attachments: AttachmentPayload[]): void {
    this.payload = attachments;
  }

  protected async uploadFiles(): Promise<void> {
    if (! this.payload) {
      return;
    }
    this.isLoading = true;
    for (let i = 0; i < this.payload.length; i += 1) {
      if (this.isEvaluation) {
      // eslint-disable-next-line no-await-in-loop
        await new Report(this.report).uploadEvaluation(this.payload[i])
          .then(() => {
            this.handleFileUploaded(i);
          })
          .catch((error: AxiosError) => {
            ErrorHandler.network(error);
            return null;
          });
      } else {
      // eslint-disable-next-line no-await-in-loop
        await new Report(this.report).addMedia(this.payload[i])
          .then(() => {
            this.handleFileUploaded(i);
          })
          .catch((error: AxiosError) => {
            ErrorHandler.network(error);
            return null;
          });
      }
    }
  }

  protected handleFileUploaded(index: number): void {
    if (this.isEvaluation) {
      this.$emit('evaluationUploaded');
      this.close();
    }

    if (this.payload.length === (index + 1)) {
      this.$emit('fileUploaded');
      this.close();
    }
  }

  protected async updateAttachment(): Promise<void> {
    if (! this.attachment || ! this.report) {
      return;
    }

    const payload: {[key: string]: number | string | boolean | null} = {
      description: this.attachment.description ? this.attachment.description : '',
      hin_show_to_all: this.attachment.hin_show_to_all || false,
      kpi_points: this.attachment.kpi_points || false,
      amount_of_kpi_points: this.attachment.amount_of_kpi_points || null,
    };

    await this.report
      .updateMediaEndpoint(this.attachment.uuid ? this.attachment.uuid : '', payload)
      .then(() => {
        this.$emit('fileUploaded');
        this.close();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected get showToAll(): boolean {
    if (! this.attachment) {
      return false;
    }
    return this.isRequired;
  }

  protected get isRequired(): boolean {
    if (! this.attachment || ! this.attachment.type) {
      return false;
    }
    return bureauAttachmentTypes.includes(this.attachment.type);
  }

  protected get isEditing(): boolean {
    return !! this.attachment;
  }

  protected get hasFileUrl(): boolean {
    return !! (this.attachment && this.attachment.get_url);
  }

  protected get title(): string {
    if (this.isEvaluation) {
      if (this.hasFileUrl) {
        return 'Prestatieverklaring aanpassen';
      }
      return 'Prestatieverklaring toevoegen';
    }

    if (this.isEditing && this.hasFileUrl) {
      return 'Bijlage aanpassen';
    }

    return 'Bijlagen toevoegen';
  }

  protected get isDisabled(): boolean {
    if (this.isEditing && this.hasFileUrl) {
      return false;
    }

    return this.payload.length === 0;
  }
}
